import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../shared/components/toast/useToast';
import { referralsApi } from '../api/referralsApi';
import type {
  CreateReferralCodePayload,
  CreateReferralRewardProgramPayload,
  ReferralCodeQuery,
  RewardLedgerStatus,
  UpdateReferralCodePayload,
  UpdateReferralRewardProgramPayload,
} from '../types';

const KEY = 'referrals';

/** Nest validation errors arrive as `message: string[]`; take the first line. */
const errMsg = (err: unknown, fallback: string) => {
  const msg = (err as { message?: unknown })?.message;
  if (Array.isArray(msg)) return (msg as string[])[0] || fallback;
  return (typeof msg === 'string' ? msg : '') || fallback;
};

/** ApiFetch has already toasted anything that reached the server; only cover the rest. */
const useErrorToast = () => {
  const { toast } = useToast();
  return (fallback: string) => (err: unknown) => {
    if (!(err as { toasted?: boolean })?.toasted) toast.error(errMsg(err, fallback));
  };
};

export const useReferralCodes = (query: ReferralCodeQuery = {}) =>
  useQuery({
    queryKey: [KEY, 'codes', query],
    queryFn: () => referralsApi.listCodes(query),
    staleTime: 30 * 1000,
  });

export const useCreateReferralCode = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (payload: CreateReferralCodePayload) => referralsApi.createCode(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'codes'] });
      toast.success('Referral code created');
    },
    onError: onError('Failed to create referral code'),
  });
};

export const useUpdateReferralCode = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateReferralCodePayload }) =>
      referralsApi.updateCode(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'codes'] });
      toast.success('Referral code updated');
    },
    onError: onError('Failed to update referral code'),
  });
};

export const useAttributeReferral = () => {
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ code, contactId }: { code: string; contactId: string }) =>
      referralsApi.attribute(code, contactId),
    onSuccess: () => toast.success('Referral recorded for this contact'),
    onError: onError('Failed to record the referral'),
  });
};

export const useReferralPrograms = () =>
  useQuery({
    queryKey: [KEY, 'programs'],
    queryFn: () => referralsApi.listPrograms(),
    staleTime: 30 * 1000,
  });

export const useCreateReferralProgram = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (payload: CreateReferralRewardProgramPayload) => referralsApi.createProgram(payload),
    onSuccess: () => {
      // Making one program the default demotes the previous one server-side.
      queryClient.invalidateQueries({ queryKey: [KEY, 'programs'] });
      toast.success('Reward rule created');
    },
    onError: onError('Failed to create reward rule'),
  });
};

export const useUpdateReferralProgram = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateReferralRewardProgramPayload }) =>
      referralsApi.updateProgram(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'programs'] });
      toast.success('Reward rule updated');
    },
    onError: onError('Failed to update reward rule'),
  });
};

export const useReferralRewards = (status?: RewardLedgerStatus) =>
  useQuery({
    queryKey: [KEY, 'rewards', status ?? 'all'],
    queryFn: () => referralsApi.listRewards(status),
    staleTime: 30 * 1000,
  });

export const useUpdateRewardStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: RewardLedgerStatus }) =>
      referralsApi.updateRewardStatus(id, status),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [KEY, 'rewards'] });
      toast.success(variables.status === 'PAID' ? 'Reward marked complete' : 'Reward approved');
    },
    onError: onError('Failed to update the reward'),
  });
};
